"use client";

import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient, type QueryObserverResult } from "@tanstack/react-query";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { useSeizeSettingsOptional } from "@/contexts/SeizeSettingsContext";
import { pinnedWavesApi } from "@/services/api/pinned-waves-api";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWavesPinFilter } from "@/generated/models/ApiWavesPinFilter";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
export const MAX_PINNED_WAVES = 20;

// Cache time constants for React Query
const PINNED_WAVES_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const PINNED_WAVES_GC_TIME = 10 * 60 * 1000; // 10 minutes
const PINNED_WAVES_REFETCH_INTERVAL = 2 * 60 * 1000; // 2 minutes

// Type definitions for React Query data structures
interface InfiniteQueryData<T> {
  pages: T[][];
  pageParams: unknown[];
}
interface UsePinnedWavesServerReturn {
  pinnedWaves: ApiWave[];
  pinnedIds: string[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  pinWave: (waveId: string) => Promise<void>;
  unpinWave: (waveId: string) => Promise<void>;
  refetch: () => Promise<QueryObserverResult<ApiWave[], Error>>;
  isOperationInProgress: (waveId: string) => boolean;
  canPinWave: (waveId: string) => boolean;
}
export function usePinnedWavesServer(): UsePinnedWavesServerReturn {
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const seizeSettings = useSeizeSettingsOptional();
  const queryClient = useQueryClient();

  // Track ongoing operations to prevent concurrent pins
  const ongoingOperations = useRef<Set<string>>(new Set());

  // Only fetch if user is authenticated
  const isAuthenticated = !!connectedProfile?.handle && !activeProfileProxy;
  const viewerIdentityKey = address?.toLowerCase() ?? null;

  // Define the specific query key as a constant
  const PINNED_WAVES_QUERY_KEY = useMemo(() => [QueryKey.WAVES_OVERVIEW, {
    pinned: ApiWavesPinFilter.Pinned,
    ...(viewerIdentityKey ? {
      viewer_identity: viewerIdentityKey
    } : {})
  }] as const, [viewerIdentityKey]);

  // Fetch pinned waves
  const {
    data: pinnedWaves = [],
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: PINNED_WAVES_QUERY_KEY,
    queryFn: pinnedWavesApi.fetchPinnedWaves,
    enabled: isAuthenticated,
    staleTime: PINNED_WAVES_STALE_TIME,
    gcTime: PINNED_WAVES_GC_TIME,
    refetchInterval: PINNED_WAVES_REFETCH_INTERVAL
  });

  // Clear pinned waves data when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear pinned waves data immediately when user logs out
      queryClient.setQueryData(PINNED_WAVES_QUERY_KEY, []);
    }
  }, [isAuthenticated, PINNED_WAVES_QUERY_KEY, queryClient]);

  // Derive pinned IDs from pinned waves with stable identity
  const pinnedIds = useMemo(() => pinnedWaves.map(wave => wave.id), [pinnedWaves]);
  const countsTowardPinBudget = useCallback((waveId: string) => !seizeSettings?.isAnnouncementsWave(waveId), [seizeSettings]);
  const pinnedBudgetCount = useMemo(() => pinnedIds.filter(countsTowardPinBudget).length, [pinnedIds, countsTowardPinBudget]);
  const getOngoingPinCount = useCallback((waveId_0: string) => {
    let ongoingPinCount = 0;
    ongoingOperations.current.forEach(id => {
      if (id === waveId_0) {
        return;
      }
      if (!pinnedIds.includes(id) && countsTowardPinBudget(id)) {
        ongoingPinCount++;
      }
    });
    return ongoingPinCount;
  }, [pinnedIds, countsTowardPinBudget]);
  const canPinWave = useCallback((waveId_1: string) => {
    if (pinnedIds.includes(waveId_1)) {
      return true;
    }
    return pinnedBudgetCount + getOngoingPinCount(waveId_1) < MAX_PINNED_WAVES;
  }, [pinnedIds, pinnedBudgetCount, getOngoingPinCount]);

  // Shared invalidation logic for both pin and unpin operations
  const invalidateWavesQueries = useCallback(() => {
    // Invalidate specific queries only
    queryClient.invalidateQueries({
      queryKey: PINNED_WAVES_QUERY_KEY
    });
    // Also invalidate main waves to update isPinned status
    queryClient.invalidateQueries({
      queryKey: [QueryKey.WAVES_OVERVIEW],
      predicate: query => {
        // Only invalidate main waves queries, not pinned waves
        const [key, params] = query.queryKey;
        if (key !== QueryKey.WAVES_OVERVIEW || typeof params !== "object" || params === null) {
          return false;
        }
        if (Reflect.get(params, "pinned")) {
          return false;
        }
        const viewerIdentityParam = Reflect.get(params, "viewer_identity");
        const queryViewerIdentity = typeof viewerIdentityParam === "string" ? viewerIdentityParam : null;
        return queryViewerIdentity === viewerIdentityKey;
      }
    });
  }, [queryClient, PINNED_WAVES_QUERY_KEY, viewerIdentityKey]);

  // Pin wave mutation
  const pinMutation = useMutation({
    mutationFn: pinnedWavesApi.pinWave,
    onMutate: async (waveId_2: string) => {
      // Cancel only the specific pinned waves query
      await queryClient.cancelQueries({
        queryKey: PINNED_WAVES_QUERY_KEY
      });
      const previousPinnedWaves = queryClient.getQueryData<ApiWave[]>(PINNED_WAVES_QUERY_KEY);

      // Try to find the wave object in other queries for optimistic update
      let waveToPin: ApiWave | undefined;

      // Search through all waves overview queries to find the wave
      queryClient.getQueriesData<ApiWave[] | InfiniteQueryData<ApiWave>>({
        queryKey: [QueryKey.WAVES_OVERVIEW]
      }).forEach(([_, data]) => {
        if (data && !waveToPin) {
          // Type guard for array data
          if (Array.isArray(data)) {
            const waves = data;
            waveToPin = waves.find((wave_0): wave_0 is ApiWave => wave_0.id === waveId_2);
          } else if (typeof data === "object" && "pages" in data) {
            // Type guard for infinite query structure

            for (const page of data.pages) {
              waveToPin = page.find((wave_1): wave_1 is ApiWave => wave_1.id === waveId_2);
              if (waveToPin) break;
            }
          }
        }
      });

      // If we found the wave, optimistically add it to pinned waves
      if (waveToPin && previousPinnedWaves) {
        // Check if wave is already pinned to avoid duplicates
        const isAlreadyPinned = previousPinnedWaves.some(wave_2 => wave_2.id === waveId_2);
        if (!isAlreadyPinned) {
          const optimisticWave: ApiWave = {
            ...waveToPin,
            pinned: true
          };
          queryClient.setQueryData(PINNED_WAVES_QUERY_KEY, [optimisticWave, ...previousPinnedWaves]);
        }
      }
      return {
        previousPinnedWaves
      };
    },
    onError: (err, __0, context) => {
      // Revert on error if we had previous data
      if (context?.previousPinnedWaves) {
        queryClient.setQueryData(PINNED_WAVES_QUERY_KEY, context.previousPinnedWaves);
      }
      console.error("Error pinning wave:", err);
    },
    onSuccess: invalidateWavesQueries
  });

  // Unpin wave mutation
  const unpinMutation = useMutation({
    mutationFn: pinnedWavesApi.unpinWave,
    onMutate: async (waveId_3: string) => {
      // Cancel only the specific pinned waves query
      await queryClient.cancelQueries({
        queryKey: PINNED_WAVES_QUERY_KEY
      });
      const previousPinnedWaves_0 = queryClient.getQueryData<ApiWave[]>(PINNED_WAVES_QUERY_KEY);

      // Optimistic update - remove from pinned waves immediately
      if (previousPinnedWaves_0) {
        queryClient.setQueryData(PINNED_WAVES_QUERY_KEY, previousPinnedWaves_0.filter(wave_3 => wave_3.id !== waveId_3));
      }
      return {
        previousPinnedWaves: previousPinnedWaves_0
      };
    },
    onError: (err_0, __1, context_0) => {
      // Revert optimistic update
      if (context_0?.previousPinnedWaves) {
        queryClient.setQueryData(PINNED_WAVES_QUERY_KEY, context_0.previousPinnedWaves);
      }
      console.error("Error unpinning wave:", err_0);
    },
    onSuccess: invalidateWavesQueries
  });
  const pinWave = useCallback(async (waveId_4: string) => {
    // Prevent concurrent operations on same wave
    if (ongoingOperations.current.has(waveId_4)) {
      throw new Error("Operation already in progress for this wave");
    }
    if (!canPinWave(waveId_4)) {
      throw new Error(`Maximum ${MAX_PINNED_WAVES} pinned waves allowed`);
    }

    // Mark operation as ongoing (synchronous)
    ongoingOperations.current.add(waveId_4);
    try {
      await pinMutation.mutateAsync(waveId_4);
    } finally {
      // Always clean up the operation tracking
      ongoingOperations.current.delete(waveId_4);
    }
  }, [canPinWave, pinMutation]);
  const unpinWave = useCallback(async (waveId_5: string) => {
    // Prevent concurrent operations on same wave
    if (ongoingOperations.current.has(waveId_5)) {
      throw new Error("Operation already in progress for this wave");
    }

    // Mark operation as ongoing (synchronous)
    ongoingOperations.current.add(waveId_5);
    try {
      await unpinMutation.mutateAsync(waveId_5);
    } finally {
      ongoingOperations.current.delete(waveId_5);
    }
  }, [unpinMutation]);
  return {
    pinnedWaves,
    pinnedIds,
    isLoading,
    isError: isError,
    error: error ?? pinMutation.error ?? unpinMutation.error,
    pinWave,
    unpinWave,
    refetch,
    isOperationInProgress: (waveId_6: string) => ongoingOperations.current.has(waveId_6),
    canPinWave
  };
}